import { ComponentType, useContext, useEffect, useMemo, useRef } from "react";
import { createRoot } from "react-dom/client";
import { AsyncJobsProvider } from "@kaltura/mediaspace-shared-contexts/AsyncJobsProvider";
import {
    EmbeddedCncWidgetContextProvider,
    useEmbeddedCncWidgetContext,
} from "@kaltura/mediaspace-shared-contexts/EmbeddedCncWidgetContext";
import { KmsUnisphereProvider } from "@kaltura/mediaspace-shared-contexts/KmsUnisphereProvider";
import { Config } from "@kaltura/mediaspace-shared-data-kms-config/Config";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import { ConfigSidebarButton } from "@kaltura/mediaspace-shared-data-kms-config/ConfigSidebarButton";
import {
    SidebarButtonProps,
    SidebarLayout,
    SidebarLayoutProps,
    useSidebarLayoutContext,
} from "@kaltura/ds-react-layouts";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { styled } from "@mui/material";
import { AsyncJobsChecker } from "./async-jobs";
import { getComponentFromPath, kmsCookie } from "@kaltura/mediaspace-shared-utils";
import { UnisphereNotificationsProvider } from "@unisphere/notifications-runtime-react";
import { TopLevelComponentWrappers } from "./TopLevelComponentWrappers";
import { SidebarRailToggle } from "./SidebarRailToggle";

// Session cookie (no expiry) — cleared when the browser closes, which aligns with a new login session.
const SIDEBAR_COOKIE = "sidebar_closed";
const isClosedByUser = (): boolean => !!kmsCookie(SIDEBAR_COOKIE);
const persistClosedByUser = () => kmsCookie(SIDEBAR_COOKIE, "1", { path: "/" });
const persistOpenedByUser = () => kmsCookie(SIDEBAR_COOKIE, null, { path: "/" });

export interface DsLayoutProps<T extends object> extends Omit<KmsTypeComponent<T>, "name"> {
    context: Config;
}

// make the main region 100% height
const StyledMainRegionWrapper = styled("div")({
    display: "flex",
    flexDirection: "column",
    minHeight: "inherit",
});

const StyledMainContentRegionWrapper = styled("div")({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
});

type SidebarLayoutWrapperProps = Required<
    Pick<SidebarLayoutProps, "mainRegion" | "floatedRegion" | "sidebarBottomButtonsRegion">
> &
    Pick<SidebarLayoutProps, "sidebarButtons" | "defaultButtonId"> & {
        hasSidebarButtons: boolean;
    };

/**
 * Inner component that renders SidebarLayout and has access to EmbeddedCncWidgetContext
 */
const SidebarLayoutWrapper = ({
    mainRegion,
    floatedRegion,
    sidebarButtons,
    defaultButtonId,
    sidebarBottomButtonsRegion,
    hasSidebarButtons,
}: SidebarLayoutWrapperProps) => {
    const { isRailCollapsed } = useEmbeddedCncWidgetContext();

    // Show the buttons rail only when not collapsed and there are buttons that should be shown
    const showButtonsRail = !isRailCollapsed && hasSidebarButtons;

    return (
        <SidebarLayout
            mainRegion={mainRegion}
            floatedRegion={
                <>
                    {floatedRegion}
                    {hasSidebarButtons && <SidebarRailToggle />}
                </>
            }
            sidebarButtons={sidebarButtons}
            defaultOpen={!!defaultButtonId && !isClosedByUser()}
            defaultButtonId={defaultButtonId}
            sidebarBottomButtonsRegion={sidebarBottomButtonsRegion}
            showButtonsRail={showButtonsRail}
        />
    );
};

/**
 * Owns the two parts of the sidebar's open/closed state that live outside SidebarLayout:
 *
 * 1. Persistence - the state is stored in a session cookie so DsLayout can suppress auto-open on
 *    the next page load when the user previously closed the sidebar.
 * 2. Keeping the contents drawer in sync with the buttons rail - collapsing the rail only hides
 *    the buttons, while a drawer opened from it keeps its own state and, with the rail gone,
 *    stretches across the whole viewport at the reflow breakpoint, leaving the expand button
 *    floating on top of the very panel the user asked to hide.
 *
 * The two are handled together on purpose: (2) opens and closes the drawer on the user's behalf,
 * and those changes must not be persisted by (1). Collapsing follows the viewport as well as the
 * toggle button, so persisting it would let a transient zoom change rewrite a stored preference.
 *
 * Must be rendered inside SidebarLayout so it can access useSidebarLayoutContext.
 */
const SidebarStateManager = () => {
    const { isOpened, setIsOpened } = useSidebarLayoutContext();
    const { isRailCollapsed, setIsRailCollapsed } = useEmbeddedCncWidgetContext();
    // Tells "the rail just collapsed under an open drawer" apart from "something opened the drawer
    // while the rail was already collapsed" - the same state, but opposite resolutions. Starts out
    // false so a rail that is already collapsed on mount counts as having just collapsed.
    const wasRailCollapsedRef = useRef(false);
    // Marks the close below as ours, so the persistence effect can tell it apart from a close the
    // user asked for.
    const closedByRailCollapseRef = useRef(false);

    useEffect(() => {
        const railJustCollapsed = isRailCollapsed && !wasRailCollapsedRef.current;
        wasRailCollapsedRef.current = isRailCollapsed;

        if (!isRailCollapsed || !isOpened) {
            return;
        }

        if (railJustCollapsed) {
            // Collapsing always frees the content area, whether it was triggered by the toggle
            // button or by the viewport entering the breakpoint.
            closedByRailCollapseRef.current = true;
            setIsOpened(false);
        }
        else {
            // The drawer was opened while the rail was collapsed, which only happens through a
            // control outside the rail (e.g. the comments button in the media page title area).
            // Bring the rail back instead of closing the drawer again, which would leave those
            // controls looking dead.
            setIsRailCollapsed(false);
        }
    }, [isRailCollapsed, isOpened, setIsOpened, setIsRailCollapsed]);

    useEffect(() => {
        if (!isOpened && closedByRailCollapseRef.current) {
            closedByRailCollapseRef.current = false;
            return;
        }

        if (isOpened) {
            persistOpenedByUser();
        }
        else {
            persistClosedByUser();
        }
    }, [isOpened]);

    return null;
};

/**
 * Render a component in a full-react layout (the whole page is rendered here, including the header and the footer).
 * All layout-specific components are supposed to come from the context.
 *
 * Regions illustration: https://i.gyazo.com/b47c2860d2404292c1ac0585f78ba5e6.png
 */
export const DsLayout = <T extends object>({ context, ...mainComponent }: DsLayoutProps<T>) => {
    const { dsLayout } = context;

    const renderRegion = (items: Omit<KmsTypeComponent, "name">[] = [], keyPrefix = "") =>
        items
            .map((item, index) => ({ ...item, index }))
            // sort by order, preserve original order when the order is the same
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.index - b.index)
            .map(({ component, props, index }) => {
                const Component = getComponentFromPath(component);
                return <Component key={keyPrefix + index} {...props} />;
            });

    const configContext = useContext(ConfigContext);
    const mainAreaAttributes = !configContext.application?.isKAF
        ? {
              id: "contentWrap",
              role: "main",
              tabIndex: -1,
          }
        : {};
    const renderMainRegion = () => {
        return (
            <StyledMainRegionWrapper className={"kms-ds-main-region"}>
                {renderRegion(dsLayout?.components.header, "header")}
                <StyledMainContentRegionWrapper {...mainAreaAttributes}>
                    {renderRegion([mainComponent, ...(dsLayout?.components.main ?? [])], "main")}
                </StyledMainContentRegionWrapper>
                {renderRegion(dsLayout?.components.footer, "footer")}
            </StyledMainRegionWrapper>
        );
    };

    const defaultButtonId = dsLayout?.defaultSidebarTopButtonId;

    const formattedButtons = useMemo(
        () =>
            dsLayout?.sidebarTopButtons?.map(
                <ButtonProps, ContentsProps extends object>({
                    buttonDescriptor: { contentsComponent, ...button },
                    contentsProps,
                }: ConfigSidebarButton<ButtonProps, ContentsProps>): SidebarButtonProps<ButtonProps> => {
                    const Contents = getComponentFromPath(contentsComponent);
                    return {
                        ...button,
                        contents: <Contents {...contentsProps} />,
                    };
                }
            ),
        [dsLayout?.sidebarTopButtons]
    );

    const hasSidebarButtons = dsLayout?.sidebarTopButtons?.some(({ showInRail }) => showInRail !== false) ?? false;

    return (
        <TopLevelComponentWrappers context={context}>
            <KmsUnisphereProvider>
                <EmbeddedCncWidgetContextProvider>
                    <AsyncJobsProvider initValue={context.application.shouldCheckAsyncJobsStatus}>
                        <UnisphereNotificationsProvider>
                            <AsyncJobsChecker />
                            <SidebarLayoutWrapper
                                mainRegion={
                                    <>
                                        {renderMainRegion()}
                                        <SidebarStateManager />
                                    </>
                                }
                                floatedRegion={renderRegion(dsLayout?.components.floated)}
                                sidebarButtons={formattedButtons}
                                defaultButtonId={defaultButtonId}
                                sidebarBottomButtonsRegion={renderRegion(dsLayout?.components.sidebarBottomButtons)}
                                hasSidebarButtons={hasSidebarButtons}
                            />
                        </UnisphereNotificationsProvider>
                    </AsyncJobsProvider>
                </EmbeddedCncWidgetContextProvider>
            </KmsUnisphereProvider>
        </TopLevelComponentWrappers>
    );
};

/**
 * Render component in full-react layout.
 * This function could be used directly on the PHP side, e.g.
 * ```
 * <div id="some-id"></div>
 * <script>
 *     MEDIASPACE.DsLayout.renderPageComponent(
 *         'some-id',
 *         MEDIASPACE.AppName.ComponentReference,
 *         <?php echo $this->jsCodeEcho($this->props + ['context' => $configContext]); ?>
 *     );
 * </script>
 * ```
 */
export const renderPageComponent = <T extends object>(
    id: string,
    component: ComponentType<T>,
    props: T & { context: Config },
    order?: number
) => {
    const root = createRoot(document.getElementById(id)!);
    root.render(<DsLayout context={props.context} component={component} props={props as T} order={order} />);
};

/**
 * Return a render function for rendering component in full-react layout.
 * This helper could be used in a React app to prepare a page render function for the client side.
 * The render function takes element ID as the first argument, and component's props with a context object as the second argument,
 * so it would be used like the following on the PHP side:
 * ```
 * <div id="some-id"></div>
 * <script>
 *     MEDIASPACE.AppName.render('some-id', <?php echo $this->jsCodeEcho($this->props + ['context' => $configContext]); ?>);
 * </script>
 * ```
 */
export const getDsLayoutComponentRenderer =
    <T extends object>(component: ComponentType<T>, order?: number) =>
    (id: string, props: T & { context: Config }) =>
        renderPageComponent(id, component, props, order);
